import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Grid,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  School as CoachIcon,
  Business as ClubIcon,
  EmojiEvents as TrophyIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { getApiBaseUrlWithFallback, REGISTRATION_BASE_URL, API_BASE_URL } from '../config/environment';

// Import components that will be used with interclub filtering
import InterclubUserManagement from '../components/InterclubUserManagement';
import InterclubCompetitions from '../components/InterclubCompetitions';
import InterclubClubs from '../components/InterclubClubs';
import InterclubSkaters from '../components/InterclubSkaters';
import InterclubCoaches from '../components/InterclubCoaches';
import InterclubRoleApprovals from '../components/InterclubRoleApprovals';
import InterclubBilling from '../components/InterclubBilling';
import InterclubSettings from '../components/InterclubSettings';
import SeriesSettings from '../components/SeriesSettings';
import Tools from './Tools';

export default function InterclubAdmin() {
  const { user, isApprovedInterclubAdmin, adminStatusLoading, getUserInterclubId } = useFirebaseAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [interclub, setInterclub] = useState(null);
  const [loading, setLoading] = useState(true);

  const interclubId = getUserInterclubId();
  const isInterclubSection = location.pathname.startsWith('/interclub-admin/interclub');

  useEffect(() => {
    const loadInterclub = async () => {
      if (!interclubId) {
        setLoading(false);
        return;
      }

      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const response = await fetch(`${baseUrl}/interclubs/${interclubId}`);
        if (response.ok) {
          const data = await response.json();
          setInterclub(data);
        }
      } catch (error) {
        console.error('Failed to load interclub:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInterclub();
  }, [interclubId]);

  // If still loading admin status, show loading
  if (adminStatusLoading || loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // If not logged in or not an approved interclub admin, redirect to home
  if (!user || !isApprovedInterclubAdmin()) {
    navigate('/');
    return null;
  }

  if (!interclubId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your account is not associated with an interclub.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To manage an interclub (clubs, skaters, coaches, and series), request the Interclub Admin role from your Profile—select your interclub and submit the role request. Existing interclub admins can approve your request.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', pl: 0 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'primary.200', backgroundColor: 'primary.50' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            {isInterclubSection ? (interclub?.name || 'My Interclub') : 'My Series'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isInterclubSection
              ? 'Clubs, skaters, coaches, and settings for your interclub only.'
              : 'Series-level dashboard and competitions across participating interclubs.'}
          </Typography>
        </Box>
      </Paper>

      {/* Routes: Series (dashboard, competitions, billing, settings) and My Interclub (dashboard, clubs, skaters, ...) */}
      <Routes>
        <Route path="/" element={<SeriesDashboard interclub={interclub} interclubId={interclubId} />} />
        <Route path="/competitions" element={<InterclubCompetitions interclubId={interclubId} />} />
        <Route path="/billing" element={<InterclubBilling interclubId={interclubId} />} />
        <Route path="/settings" element={<SeriesSettings interclubId={interclubId} />} />
        <Route path="/interclub" element={<InterclubOverview interclub={interclub} interclubId={interclubId} pathPrefix="/interclub-admin/interclub" />} />
        <Route path="/interclub/users" element={<InterclubUserManagement interclubId={interclubId} interclub={interclub} />} />
        <Route path="/interclub/clubs" element={<InterclubClubs interclubId={interclubId} interclub={interclub} />} />
        <Route path="/interclub/skaters" element={<InterclubSkaters interclubId={interclubId} interclub={interclub} />} />
        <Route path="/interclub/coaches" element={<InterclubCoaches interclubId={interclubId} interclub={interclub} />} />
        <Route path="/interclub/role-approvals" element={<InterclubRoleApprovals interclubId={interclubId} />} />
        <Route path="/interclub/settings" element={<InterclubSettings interclubId={interclubId} />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="*" element={<Navigate to="/interclub-admin" replace />} />
      </Routes>
    </Box>
  );
}

// Series-level dashboard: host sees all participating interclubs' clubs/skaters; links to My Interclub and Series Competitions
function SeriesDashboard({ interclub, interclubId }) {
  return (
    <InterclubOverview
      interclub={interclub}
      interclubId={interclubId}
      pathPrefix="/interclub-admin/interclub"
      competitionsPath="/interclub-admin/competitions"
      useSeriesDashboard
    />
  );
}

// Stat Card Component
function StatCard({ title, value, subtitle, icon, color = 'primary', trend, onClick, linkText, onLinkClick }) {
  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2.5, 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderColor: `${color}.main`,
        } : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main` }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Chip 
              size="small" 
              label={trend} 
              color={trend.startsWith('+') ? 'success' : 'default'}
              sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
          {linkText && onLinkClick && (
            <Typography 
              component="span"
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                onLinkClick();
              }}
              sx={{ 
                display: 'block',
                mt: 0.5,
                color: 'primary.main',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {linkText} →
            </Typography>
          )}
        </Box>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: `${color}.50`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
}

// Quick Action Card Component
function QuickActionCard({ title, description, icon, color = 'primary', onClick }) {
  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2, 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderColor: `${color}.main`,
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ 
        p: 1, 
        borderRadius: 2, 
        bgcolor: `${color}.50`,
        display: 'flex',
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <ArrowIcon color="action" fontSize="small" />
    </Paper>
  );
}

// Interclub Overview/Dashboard Component (used for both Series and My Interclub dashboard)
// pathPrefix: base path for links. competitionsPath: optional override for competitions link.
// useSeriesDashboard: when true, request ?series=true so host sees all participating interclubs' clubs/skaters.
function InterclubOverview({ interclub, interclubId, pathPrefix = '/interclub-admin', competitionsPath, useSeriesDashboard }) {
  const navigate = useNavigate();
  const { getIdToken } = useFirebaseAuth();
  const { getHeaders } = useImpersonation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const path = (suffix) => (suffix === 'competitions' && competitionsPath ? competitionsPath : `${pathPrefix}/${suffix}`);
  // When host is viewing My Series dashboard, links to skaters/coaches/clubs include ?series=true so list shows all participating interclubs
  const pathWithSeries = (suffix, queryParams = '') => {
    const base = path(suffix);
    const params = new URLSearchParams(queryParams);
    if (useSeriesDashboard && (suffix === 'skaters' || suffix === 'coaches' || suffix === 'clubs')) params.set('series', 'true');
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const loadDashboard = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Unable to get authentication token');

      const url = `${REGISTRATION_BASE_URL}/interclub/${interclubId}/dashboard${useSeriesDashboard ? '?series=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load dashboard data');
      }

      const data = await response.json();
      setDashboard(data.dashboard);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, getIdToken, getHeaders, useSeriesDashboard]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleTileClick = (targetPath) => {
    navigate(targetPath);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleViewStripe = async () => {
    const stripeAccountId = dashboard?.financial?.stripeAccountId;
    if (!stripeAccountId) return;

    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/stripe/accounts/${stripeAccountId}/dashboard-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get Stripe dashboard link');
      }

      const data = await response.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error opening Stripe dashboard:', err);
      alert('Unable to open Stripe dashboard. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  const paymentProgress = dashboard?.skaters?.total > 0
    ? (dashboard.skaters.paid / dashboard.skaters.total) * 100
    : 0;

  return (
    <Box>
      {/* Header with Season Info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dashboard
          </Typography>
          {dashboard?.season && (
            <Typography variant="body2" color="text.secondary">
              {dashboard.season.name} Season
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadDashboard} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Key Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Skaters"
            value={dashboard?.skaters?.total || 0}
            subtitle={`${dashboard?.skaters?.paid || 0} paid`}
            icon={<PeopleIcon sx={{ color: 'primary.main' }} />}
            color="primary"
            onClick={() => handleTileClick(pathWithSeries('skaters'))}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Coaches"
            value={dashboard?.coaches?.total || 0}
            subtitle={`${dashboard?.coaches?.withSkaters || 0} with skaters`}
            icon={<CoachIcon sx={{ color: 'info.main' }} />}
            color="info"
            onClick={() => handleTileClick(pathWithSeries('coaches'))}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Clubs"
            value={dashboard?.clubs?.active || 0}
            subtitle={`of ${dashboard?.clubs?.total || 0} total`}
            icon={<ClubIcon sx={{ color: 'secondary.main' }} />}
            color="secondary"
            onClick={() => handleTileClick(pathWithSeries('clubs'))}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Competitions"
            value={dashboard?.competitions?.total || 0}
            subtitle={`${dashboard?.competitions?.upcoming || 0} upcoming`}
            icon={<TrophyIcon sx={{ color: 'warning.main' }} />}
            color="warning"
            onClick={() => handleTileClick(path('competitions'))}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Users"
            value={dashboard?.users?.total || 0}
            subtitle={dashboard?.users?.pendingApprovals > 0 
              ? `${dashboard.users.pendingApprovals} pending`
              : 'All approved'}
            icon={<PeopleIcon sx={{ color: 'success.main' }} />}
            color="success"
            onClick={() => handleTileClick(path('users'))}
          />
        </Grid>
        {useSeriesDashboard && (
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Est. Revenue"
              value={formatCurrency(dashboard?.financial?.netRevenue || 0)}
              subtitle={`~${formatCurrency(dashboard?.financial?.netPerSkater || 0)}/skater after fees`}
              icon={<MoneyIcon sx={{ color: 'success.main' }} />}
              color="success"
              onClick={() => handleTileClick(path('billing'))}
              linkText={dashboard?.financial?.stripeAccountId ? "View in Stripe" : null}
              onLinkClick={dashboard?.financial?.stripeAccountId ? handleViewStripe : null}
            />
          </Grid>
        )}
      </Grid>

      {/* Series: show participating interclubs when 2+, or hint when only 1 */}
      {useSeriesDashboard && dashboard?.seriesParticipantCount > 1 && dashboard?.participantInterclubNames?.length > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Participating interclubs:</strong>{' '}
          {dashboard.participantInterclubNames.map((p) => p.name).join(', ')}
        </Alert>
      )}
      {useSeriesDashboard && dashboard?.seriesParticipantCount === 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This series is showing only your interclub. To include Oregon Skating Council (and others), go to <strong>My Series → Competitions</strong> → <strong>Seasons</strong> tab, edit the current season, add <strong>Pacific Northwest Inter Club</strong> and <strong>Oregon Skating Council</strong> under <strong>Participating interclubs</strong>, set <strong>Host interclub</strong> to PNIC, then save.
        </Alert>
      )}

      {/* Detailed Panels */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Registration Progress */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Registration Progress
              </Typography>
              <Chip 
                size="small" 
                label={`${Math.round(paymentProgress)}% Paid`}
                color={paymentProgress >= 80 ? 'success' : paymentProgress >= 50 ? 'warning' : 'error'}
              />
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={paymentProgress} 
              sx={{ height: 10, borderRadius: 5, mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {dashboard?.skaters?.paid || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {dashboard?.skaters?.unpaid || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Unpaid</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {(dashboard?.skaters?.paused || 0) + (dashboard?.skaters?.disqualified || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Paused/DQ</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Skaters by Level */}
          {dashboard?.skaters?.byLevel && Object.keys(dashboard.skaters.byLevel).length > 0 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Skaters by Level
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(dashboard.skaters.byLevel)
                  .sort((a, b) => b[1] - a[1])
                  .map(([level, count]) => (
                    <Chip
                      key={level}
                      label={`${level}: ${count}`}
                      variant="outlined"
                      sx={{ borderRadius: 2, cursor: 'pointer' }}
                      onClick={() => navigate(pathWithSeries('skaters', `level=${encodeURIComponent(level)}`))}
                      clickable
                    />
                  ))}
              </Box>
            </Paper>
          )}

          {/* Top Clubs */}
          {dashboard?.clubs?.topClubs?.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Clubs by Registration
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dashboard.clubs.topClubs.map((club, index) => (
                  <Box key={club.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: index === 0 ? 'warning.main' : index === 1 ? 'grey.400' : index === 2 ? '#CD7F32' : 'grey.200',
                        color: index < 3 ? 'white' : 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {club.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {club.skaterCount} skater{club.skaterCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Top 5 Coaches by Skater Count */}
          {dashboard?.coaches?.topCoaches?.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top 5 Coaches by Skaters
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dashboard.coaches.topCoaches.map((coach, index) => (
                  <Box key={coach.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: index === 0 ? 'warning.main' : index === 1 ? 'grey.400' : index === 2 ? '#CD7F32' : 'grey.200',
                        color: index < 3 ? 'white' : 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {coach.displayName}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {coach.skaterCount} skater{coach.skaterCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => handleTileClick(pathWithSeries('coaches'))}
                >
                  View all coaches →
                </Typography>
              </Box>
            </Paper>
          )}

          {/* All series clubs (multi-interclub): show every participating interclub, including those with 0 clubs */}
          {dashboard?.participantInterclubNames?.length > 1 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                All Series Clubs
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dashboard.participantInterclubNames.map(({ id: icId, name: interclubName }) => {
                  const list = (dashboard?.clubs?.seriesClubs || []).filter((c) => c.interclubId === icId);
                  return (
                    <Box key={icId}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        {interclubName}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 1 }}>
                        {list.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No clubs</Typography>
                        ) : (
                          list.map((club) => (
                            <Chip
                              key={club.id}
                              size="small"
                              label={`${club.name} (${club.skaterCount})`}
                              variant="outlined"
                              sx={{ borderRadius: 2 }}
                            />
                          ))
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Next Competition */}
          {dashboard?.competitions?.next && (
            <Paper 
              elevation={0} 
              onClick={() => handleTileClick(path('competitions'))}
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                border: '1px solid', 
                borderColor: 'warning.200',
                bgcolor: 'warning.50',
                mb: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderColor: 'warning.main',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon sx={{ color: 'warning.main' }} />
                <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  Next Competition
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {dashboard.competitions.next.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(dashboard.competitions.next.date)}
              </Typography>
              {dashboard.competitions.next.hostClub && (
                <Typography variant="caption" color="text.secondary">
                  Hosted by {dashboard.competitions.next.hostClub}
                </Typography>
              )}
            </Paper>
          )}

          {/* Pending Actions */}
          {dashboard?.users?.pendingApprovals > 0 && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                border: '1px solid', 
                borderColor: 'error.200',
                bgcolor: 'error.50',
                mb: 3,
                cursor: 'pointer',
              }}
              onClick={() => handleTileClick(path('role-approvals'))}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: 'error.main' }} />
                <Typography variant="overline" sx={{ color: 'error.main', fontWeight: 600 }}>
                  Action Required
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {dashboard.users.pendingApprovals} Pending Approval{dashboard.users.pendingApprovals !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review role requests from coaches and club admins
              </Typography>
            </Paper>
          )}

          {/* Quick Actions */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <QuickActionCard
                title="Manage Skaters"
                description="View and manage registered skaters"
                icon={<PeopleIcon sx={{ color: 'primary.main' }} fontSize="small" />}
                color="primary"
                onClick={() => handleTileClick(pathWithSeries('skaters'))}
              />
              <QuickActionCard
                title="Competitions"
                description="Add or edit competitions"
                icon={<TrophyIcon sx={{ color: 'warning.main' }} fontSize="small" />}
                color="warning"
                onClick={() => handleTileClick(path('competitions'))}
              />
              <QuickActionCard
                title="Role Approvals"
                description="Review pending requests"
                icon={<CheckIcon sx={{ color: 'success.main' }} fontSize="small" />}
                color="success"
                onClick={() => handleTileClick(path('role-approvals'))}
              />
              <QuickActionCard
                title="Settings"
                description="Configure interclub settings"
                icon={<TrendingUpIcon sx={{ color: 'secondary.main' }} fontSize="small" />}
                color="secondary"
                onClick={() => handleTileClick(path('settings'))}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
